import { Button, FormInstance, Space, Table, Tooltip } from 'antd';
import useModal from 'antd/es/modal/useModal';
import { useParams } from 'react-router-dom';
import AddEmployeeFormComponent from './add-employee-form.component';
import { useRef } from 'react';
import {
  useAddDepartmentEmployee,
  useDepartmentEmployees,
  useRemoveDepartmentEmployee,
  useUpdateDepartmentEmployee,
} from '../../../utils/api-hooks/department.hook';
import moment from 'moment';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { EmployeeResult } from '../../../utils/api-client';
import { usePagination } from '../../../shared/usePagination';
import { map } from 'lodash';
import {
  DEPARTMENT_EMPLOYEE_COMPENSATION_TYPE,
  DEPARTMENT_EMPLOYEE_COMPENSATION_TYPE_LABEL,
} from '../../../constants/department-employee';

export default function DepartmentEmployeesContainer() {
  const [modal, contextHolder] = useModal();
  const params = useParams();
  const { id } = params || {};
  const pagination = usePagination();
  const {
    data: { data, total } = {},
    isLoading,
    isFetching,
  } = useDepartmentEmployees({
    departmentId: id!,
    skip: pagination.skip,
    take: pagination.take,
  });
  const addEmployeeFormRef = useRef<
    FormInstance<{
      employeeId: string;
      amount: number;
      compensationType: 'HOURLY' | 'MONTHLY' | 'DAILY';
      defaultHours: number;
    }>
  >(null);
  const { mutateAsync: addDepartmentEmployee } = useAddDepartmentEmployee();
  const { mutateAsync: updateDepartmentEmployee } =
    useUpdateDepartmentEmployee();
  const { mutateAsync: removeDepartmentEmployee } =
    useRemoveDepartmentEmployee();

  const onAddEmployeeButtonClick = () => {
    modal.confirm({
      title: '新增成員',
      content: (
        <AddEmployeeFormComponent
          formRef={addEmployeeFormRef}
          disabledIds={map(data, 'id')}
        />
      ),
      icon: null,
      async onOk() {
        const values = await addEmployeeFormRef.current!.validateFields();

        if (!id) {
          throw new Error('Missing department id');
        }

        await addDepartmentEmployee({
          departmentId: id,
          employeeId: values.employeeId,
          amount: values.amount,
          compensationType: values.compensationType,
          defaultHours: values.defaultHours,
        });
      },
    });
  };

  const onDeleteEmployeeButtonClick = (record: EmployeeResult) => {
    modal.confirm({
      title: '刪除成員',
      width: 480,
      content: (
        <Space direction="vertical">
          <span>此動只會將該員工從此部門移除，並不會刪除該員工的資料</span>
          <span>
            確定要將此 <b>{record.name}</b> 從部門移除嗎？
          </span>
        </Space>
      ),
      async onOk() {
        if (!id) {
          throw new Error('Missing department id');
        }

        return removeDepartmentEmployee({
          departmentId: id,
          employeeId: record.id,
        });
      },
    });
  };

  const onUpdateEmployeeButtonClick = (record: EmployeeResult) => {
    modal.confirm({
      title: '更新成員',
      content: (
        <AddEmployeeFormComponent
          formRef={addEmployeeFormRef}
          isAfterEmployeeCreated
          employeeId={record.id}
          departmentId={id}
          amount={record.salaryInfo?.amount}
          compensationType={record.salaryInfo?.compensationType as any}
          defaultHours={record.salaryInfo?.defaultHours}
        />
      ),
      icon: null,
      async onOk() {
        const values = await addEmployeeFormRef.current!.validateFields();

        if (!id) {
          throw new Error('Missing department id');
        }

        await updateDepartmentEmployee([
          id,
          values.employeeId,
          values.amount,
          values.compensationType,
          values.defaultHours,
        ]);
      },
    });
  };

  return (
    <>
      {contextHolder}
      <Table
        loading={isLoading || isFetching}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        size="small"
        dataSource={data}
        pagination={{
          ...pagination,
          total,
        }}
        title={() => (
          <Button type="primary" onClick={onAddEmployeeButtonClick}>
            新增成員
          </Button>
        )}
        columns={[
          {
            title: '姓名',
            dataIndex: 'name',
          },
          {
            title: '綽號',
            dataIndex: 'nickname',
          },
          {
            title: '生日',
            dataIndex: 'birthday',
            render(birthday: string) {
              return moment(birthday).utcOffset(8).format('YYYY/MM/DD');
            },
          },
          {
            title: '薪資',
            dataIndex: 'salaryInfo',
            render(salaryInfo: { amount: number; compensationType: string }) {
              return `${salaryInfo?.amount.toLocaleString('zh-TW', {
                style: 'currency',
                currency: 'TWD',
                minimumFractionDigits: 0,
              })}/${
                DEPARTMENT_EMPLOYEE_COMPENSATION_TYPE_LABEL[
                  salaryInfo?.compensationType as keyof typeof DEPARTMENT_EMPLOYEE_COMPENSATION_TYPE_LABEL
                ]
              }`;
            },
          },
          {
            title: '預設工時',
            dataIndex: ['salaryInfo', 'defaultHours'],
          },
          {
            title: '薪資變更紀錄',
            dataIndex: 'salaryInfoHistory',
            render(
              salaryInfoHistory: {
                time: string;
                amount: number;
                compensationType: DEPARTMENT_EMPLOYEE_COMPENSATION_TYPE;
              }[]
            ) {
              return (
                <Space direction="vertical" size={0}>
                  {map(salaryInfoHistory, (item) => (
                    <span key={item.time}>
                      {moment(item.time).utcOffset(8).format('YYYY-MM-DD')} -{' '}
                      {item.amount.toLocaleString('zh-TW', {
                        style: 'currency',
                        currency: 'TWD',
                        minimumFractionDigits: 0,
                      })}
                      /
                      {
                        DEPARTMENT_EMPLOYEE_COMPENSATION_TYPE_LABEL[
                          item.compensationType
                        ]
                      }
                    </span>
                  ))}
                </Space>
              );
            },
          },
          {
            title: '操作',
            dataIndex: 'id',
            width: 50,
            render: (value: string, record) => {
              return (
                <Space size="small">
                  <Tooltip title="更新">
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      shape="circle"
                      onClick={() => onUpdateEmployeeButtonClick(record)}
                    />
                  </Tooltip>
                  <Tooltip title="刪除">
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      shape="circle"
                      onClick={() => onDeleteEmployeeButtonClick(record)}
                    />
                  </Tooltip>
                </Space>
              );
            },
          },
        ]}
      />
    </>
  );
}
